/**
 * The enrollment ceremony, from the station computer's side.
 *
 * An administrator issues a code and reads it out; somebody standing at this
 * machine types it in. The machine generates a key pair it cannot export,
 * registers the public half against that code, and keeps everything else.
 *
 * The order matters: the key pair is generated *before* the code is spent, and
 * the identity is written to IndexedDB before this resolves. A code redeemed
 * against a key that was then lost would leave the server believing a computer
 * is enrolled that cannot sign anything, and the only repair is an
 * administrator noticing and issuing another.
 */
import { ApiError } from "@/app/api-client"
import { DEVICE_PATHS, sessionRequest } from "./client"
import type { DeviceIdentity, EnrollmentResponse } from "./identity"
import { generateSigningKeyPair, importOfflineSecret, isNonExtractable } from "./keypair"
import { readIdentity, writeIdentity } from "./store"

export interface EnrollmentOutcome {
  identity: DeviceIdentity
  device: EnrollmentResponse["device"]
  /** False when the browser ignored `extractable: false`. Reported, never assumed. */
  keyIsNonExtractable: boolean
}

export class EnrollmentError extends Error {
  constructor(readonly code: string, message: string) {
    super(message)
    this.name = "EnrollmentError"
  }
}

export async function enrolThisComputer(code: string, doc: Document = document): Promise<EnrollmentOutcome> {
  assertCryptoAvailable()

  const { privateKey, publicKeySpki } = await generateSigningKeyPair()
  const keyIsNonExtractable = await isNonExtractable(privateKey)

  const response = await redeem(code, publicKeySpki, doc)

  const identity: DeviceIdentity = {
    deviceUuid: response.device.uuid,
    shortCode: response.device.shortCode,
    stationCode: response.device.stationCode,
    privateKey,
    offlineSecret: await importOfflineSecret(response.offlineSecret),
    credential: response.credential
  }

  await writeIdentity(identity)

  return { identity, device: response.device, keyIsNonExtractable }
}

/** Whether this machine has already been through the ceremony. */
export async function isEnrolled(): Promise<boolean> {
  return (await readIdentity()) !== null
}

async function redeem(code: string, publicKey: string, doc: Document): Promise<EnrollmentResponse> {
  try {
    return await sessionRequest<EnrollmentResponse>(DEVICE_PATHS.enroll, { code, publicKey }, doc)
  } catch (error) {
    if (error instanceof ApiError) throw new EnrollmentError(error.code, error.message)
    throw error
  }
}

/**
 * WebCrypto's `subtle` is only exposed on a secure origin. On plain HTTP the
 * property is simply missing, and every call below would fail with a
 * `TypeError` that says nothing about why — so the reason is given once, here.
 */
function assertCryptoAvailable(): void {
  if (typeof crypto !== "undefined" && crypto.subtle) return

  throw new EnrollmentError(
    "insecure_context",
    "This page must be served over HTTPS before a computer can be enrolled: " +
      "the browser only offers the cryptography this needs on a secure origin."
  )
}
